/**
 * TreatmentExportPage — "ייצוא PDF" for ONE locked treatment report.
 * Route: /dashboard/patients/:patientId/treatments/:treatmentId/export
 *
 * Two panes (PdfExportShell): layout toggles + free texts
 * (TreatmentExportSidebar) and a live paper preview (TreatmentExportPreview).
 * Export calls the `generateTreatmentPdf` Cloud Function (server loads
 * treatment / patient PII / branding itself), opens the PDF in PdfPreviewModal
 * and remembers the toggles on the therapist doc (`treatmentPdfPrefs`).
 *
 * Error codes: CK-3977 (export), CK-3978 (prefs save), CK-3979 (signature save).
 */

import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FilePdf, WarningCircle, LockOpen } from "@phosphor-icons/react";
import {
  selectClinicId,
  selectClinicProfile,
  selectTherapistName,
  selectCanExportData,
  selectIsAdmin,
  selectIsTrialExpired,
} from "../../store/slices/authSlice";
import {
  useGetTreatmentQuery,
  useGetTreatmentTagsQuery,
} from "../../store/services/treatmentsApi";
import { useGetPatientQuery } from "../../store/services/patientsApi";
import { useGetCategoriesQuery } from "../../store/services/tasksApi";
import { useGenerateTreatmentPdfMutation } from "../../store/services/patientReportsApi";
import { logActivity, ACTIVITY_ACTIONS, ENTITY_TYPES } from "../../firebase";
import { formatHebrewDate } from "../../utils/dateUtils";
import LoaderLoop from "../../components/shared/loader/LoaderLoop";
import SignaturePadModal from "../../components/ui/SignaturePadModal";
import PdfPreviewModal from "../../components/dashboard/reports/patientReports/PdfPreviewModal";
import PdfExportShell from "../../components/pdfExport/PdfExportShell";
import { usePdfExportSession } from "../../components/pdfExport/usePdfExportSession";
import { normalizeOptions } from "../../components/pdfExport/pdfExportOptions";
import { patientFullName } from "../../components/pdfExport/buildPaperModel";
import TreatmentExportSidebar from "../../components/treatments/export/TreatmentExportSidebar";
import TreatmentExportPreview from "../../components/treatments/export/TreatmentExportPreview";
import {
  buildTreatmentPdfModel,
  defaultTitleFor,
} from "../../components/treatments/export/buildTreatmentPdfModel";
import "./TreatmentExportPage.css";

const REPORT_TYPE_ID = "treatmentPdf";

const messageFor = (error, fallback) => {
  const msg = error?.message || "";
  // Cloud Functions surface our Hebrew HttpsError messages verbatim.
  if (/[֐-׿]/.test(msg)) return msg;
  return fallback;
};

function TreatmentExportPage() {
  const { patientId, treatmentId } = useParams();
  const navigate = useNavigate();

  const clinicId = useSelector(selectClinicId);
  const clinic = useSelector(selectClinicProfile);
  const therapistName = useSelector(selectTherapistName);
  const canExportData = useSelector(selectCanExportData);
  const isAdmin = useSelector(selectIsAdmin);
  const isTrialExpired = useSelector(selectIsTrialExpired);

  const session = usePdfExportSession({
    signatureErrorCode: "CK-3979",
    prefsErrorCode: "CK-3978",
  });
  const { therapistId, therapistProfile, savedSignatureUrl, options } = session;

  const {
    data: treatment,
    isLoading: treatmentLoading,
    isError: treatmentError,
  } = useGetTreatmentQuery({ clinicId, treatmentId }, { skip: !clinicId || !treatmentId });
  const { data: patient, isLoading: patientLoading } = useGetPatientQuery(
    { clinicId, patientId },
    { skip: !clinicId || !patientId }
  );
  const { data: treatmentTypes = [] } = useGetCategoriesQuery(
    { clinicId, type: "treatmentType" },
    { skip: !clinicId }
  );
  const { data: tags = [] } = useGetTreatmentTagsQuery({ clinicId }, { skip: !clinicId });

  const [generatePdf, { isLoading: isExporting }] = useGenerateTreatmentPdfMutation();
  const [exportError, setExportError] = useState(null);

  const typeName = useMemo(() => {
    if (!treatment?.treatmentTypeId) return "";
    return treatmentTypes.find((t) => t.id === treatment.treatmentTypeId)?.name || "";
  }, [treatment, treatmentTypes]);

  const hasLicense = !!(
    (clinic?.licenseNumber && String(clinic.licenseNumber).trim()) ||
    (therapistProfile?.licenseNumber && String(therapistProfile.licenseNumber).trim())
  );

  const model = useMemo(
    () =>
      treatment
        ? buildTreatmentPdfModel({
            treatment,
            patient,
            clinic,
            therapist: therapistProfile,
            typeName: typeName || "פגישת טיפול",
            tags,
            options: normalizeOptions(options),
          })
        : null,
    [treatment, patient, clinic, therapistProfile, typeName, tags, options]
  );

  const patientName = patientFullName(patient, treatment?.patientName);
  const treatmentPath = `/dashboard/patients/${patientId}/treatments/${treatmentId}`;

  // ─── Export ───
  const runExport = useCallback(async () => {
    setExportError(null);
    session.setPdfUrl(null);
    const normalized = normalizeOptions(options);
    const details = {
      reportTypeId: REPORT_TYPE_ID,
      treatmentId,
      signatureMode: normalized.signature,
      hasRecommendations: !!normalized.recommendations.trim(),
      hasFooterNote: !!normalized.footerNote.trim(),
    };
    const baseLog = {
      therapistId,
      therapistName: therapistName || "לא ידוע",
      action: ACTIVITY_ACTIONS.EXPORT_TREATMENT_PDF,
      entityType: ENTITY_TYPES.PATIENT,
      entityId: patientId,
      entityName: patientName,
    };
    try {
      const result = await generatePdf({
        clinicId,
        patientId,
        treatmentId,
        options: normalized,
      }).unwrap();
      session.setPdfUrl(result.pdfUrl);
      logActivity(clinicId, { ...baseLog, details });
      session.rememberPrefs(normalized);
    } catch (error) {
      console.error("[CK-3977] Error exporting treatment PDF:", {
        errorCode: "CK-3977",
        errorMessage: error?.message,
        clinicId,
        treatmentId,
        signatureMode: normalized.signature,
        timestamp: new Date().toISOString(),
      });
      setExportError(messageFor(error, "אירעה שגיאה בהפקת ה-PDF. נסו שוב בעוד רגע."));
      logActivity(clinicId, {
        ...baseLog,
        success: false,
        errorMessage: error?.message || null,
        details,
      });
    }
  }, [options, generatePdf, clinicId, patientId, treatmentId, therapistId, therapistName, patientName, session]);

  const handleExportClick = () => {
    if (isExporting || session.isSavingSignature) return;
    if (options.signature === "saved" && !savedSignatureUrl) {
      session.openPad();
      return;
    }
    runExport();
  };

  const handlePadConfirm = async (dataUrl) => {
    session.closePad();
    const ok = await session.saveSignature(dataUrl);
    if (ok && options.signature === "saved") runExport();
  };

  // ─── Guards ───
  const isLoading = !clinicId || treatmentLoading || patientLoading;
  const notExportable =
    !isLoading &&
    (treatmentError || !treatment || treatment.status !== "locked" || treatment.isDeleted === true);
  const guard = isLoading
    ? null
    : !canExportData
      ? {
          Icon: WarningCircle,
          title: "אין הרשאה לייצוא",
          text: "ייצוא נתונים אינו מופעל עבורך. פנו למנהל/ת הקליניקה.",
        }
      : notExportable
        ? {
            Icon: LockOpen,
            title: "לא ניתן לייצא תיעוד זה",
            text: "ניתן לייצא ל-PDF רק תיעוד טיפול נעול שלא נמחק.",
          }
        : null;

  const subtitle = treatment
    ? [patientName, formatHebrewDate(treatment.treatmentDate), typeName].filter(Boolean).join(" · ")
    : null;
  const footerError = exportError || session.signatureError;

  return (
    <PdfExportShell
      icon={FilePdf}
      title="ייצוא תיעוד טיפול ל-PDF"
      subtitle={subtitle}
      onBack={() => navigate(treatmentPath)}
      backLabel="חזרה לתיעוד"
      loading={isLoading}
      loadingMessage="טוען את התיעוד..."
      guard={guard}
      sidebar={
        treatment && (
          <TreatmentExportSidebar
            options={options}
            setOption={session.setOption}
            toggleOption={session.toggleOption}
            setText={session.setText}
            clinic={clinic}
            patient={patient}
            typeName={typeName}
            hasTags={(treatment.tagIds || []).some((id) => tags.some((t) => t.id === id))}
            hasVersion={Number(treatment.currentVersion) > 1}
            hasLicense={hasLicense}
            isAdmin={isAdmin}
            savedSignatureUrl={savedSignatureUrl}
            onDrawSignature={session.openPad}
            defaultTitle={defaultTitleFor(
              typeName || "פגישת טיפול",
              formatHebrewDate(treatment.treatmentDate)
            )}
          />
        )
      }
      preview={model && <TreatmentExportPreview model={model} />}
      footer={
        <>
          <div className="pdx-footer-text">
            {footerError ? (
              <span className="pdx-footer-error">
                <WarningCircle size={15} weight="bold" />
                {footerError}
              </span>
            ) : isTrialExpired ? (
              <span className="pdx-footer-error">תקופת הניסיון הסתיימה — הייצוא אינו זמין.</span>
            ) : (
              <span>ה-PDF יישמר גם בהיסטוריית הדוחות (דוחות › ייצוא דוחות).</span>
            )}
          </div>
          <button
            type="button"
            className="prw-btn primary pdx-export-btn"
            onClick={handleExportClick}
            disabled={isExporting || session.isSavingSignature || isTrialExpired}
          >
            {isExporting ? <LoaderLoop size="xs" /> : <FilePdf size={16} weight="fill" />}
            {isExporting ? "מפיק PDF…" : "ייצוא PDF"}
          </button>
        </>
      }
    >
      {session.padOpen && (
        <SignaturePadModal
          title="החתימה שלך"
          hint="חתמו באמצעות העכבר או מסך מגע. החתימה תישמר בפרופיל שלך לשימוש חוזר."
          confirmLabel="שמירה והמשך"
          onConfirm={handlePadConfirm}
          onClose={session.closePad}
        />
      )}
      {session.pdfUrl && (
        <PdfPreviewModal
          url={session.pdfUrl}
          title={model?.title || "המסמך מוכן"}
          onClose={() => session.setPdfUrl(null)}
        />
      )}
    </PdfExportShell>
  );
}

export default TreatmentExportPage;
